<template>
  <b-overlay :show="loading">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h4 class="card-title mb-0">Mis Entidades Asociadas</h4>
        <div class="d-flex gap-2 align-items-center">
          <span class="text-muted small" v-if="totalCount > 0">
            Mostrando {{ entities.length }} de {{ totalCount }} entidades
          </span>
          <b-button variant="primary" size="sm" @click="refreshList" :disabled="loading">
            <feather-icon icon="RefreshCwIcon" class="mr-1" v-if="!loading" />
            <span class="spinner-border spinner-border-sm mr-1" role="status" v-else />
            Actualizar
          </b-button>
        </div>
      </div>
      <div class="card-body">
        <!-- Mensaje cuando no hay entidades -->
        <div v-if="!loading && entities.length === 0" class="text-center py-5">
          <feather-icon icon="BuildingIcon" size="64" class="text-muted mb-3" />
          <h5 class="text-muted">No hay entidades asociadas</h5>
          <p class="text-muted">No se encontraron entidades asociadas a tu usuario.</p>
        </div>

        <!-- Tabla de entidades -->
        <b-table
          v-else
          :items="entities"
          :fields="fields"
          :busy="loading"
          :empty-text="loading ? 'Cargando entidades...' : 'No hay entidades disponibles'"
          responsive="sm"
          striped
          hover
          show-empty
        >
          <template v-slot:cell(organismo)="row">
            <div v-if="row.item.organismo">
              <strong>{{ row.item.organismo.organismoNombre }}</strong>
              <br />
              <small class="text-muted">ID: {{ row.item.organismo.organismoId }}</small>
            </div>
            <span class="text-muted" v-else>—</span>
          </template>
          <template v-slot:cell(entidad_padre)="row">
            <span v-if="row.item.entidadPadreId && row.item.entidadPadreId > 0">
              ID: {{ row.item.entidadPadreId }}
            </span>
            <span class="text-muted" v-else>—</span>
          </template>
          <template v-slot:cell(actions)="row">
            <div class="d-flex gap-1">
              <b-button
                size="sm"
                variant="outline-primary"
                @click="viewEntityDetails(row.item)"
                title="Ver detalles"
              >
                <feather-icon icon="EyeIcon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>

        <!-- Paginación -->
        <b-pagination
          v-if="totalPages > 1"
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="perPage"
          :aria-controls="false"
          align="center"
          size="sm"
          class="mt-3"
          @input="onPageChange"
        />
      </div>
    </div>

    <!-- Modal de detalles de entidad -->
    <b-modal
      v-model="showDetailModal"
      :title="selectedEntity ? `Entidad: ${selectedEntity.entidadNombre}` : 'Detalles de Entidad'"
      size="lg"
      centered
    >
      <div v-if="selectedEntity">
        <dl class="row mb-0">
          <dt class="col-sm-3">ID Entidad</dt>
          <dd class="col-sm-9">{{ selectedEntity.entidadId }}</dd>

          <dt class="col-sm-3">Nombre</dt>
          <dd class="col-sm-9">{{ selectedEntity.entidadNombre }}</dd>

          <dt class="col-sm-3">ID Entidad Padre</dt>
          <dd class="col-sm-9">
            <span v-if="selectedEntity.entidadPadreId && selectedEntity.entidadPadreId > 0">
              {{ selectedEntity.entidadPadreId }}
            </span>
            <span class="text-muted" v-else>—</span>
          </dd>

          <dt class="col-sm-3">Organismo</dt>
          <dd class="col-sm-9">
            <div v-if="selectedEntity.organismo">
              <strong>{{ selectedEntity.organismo.organismoNombre }}</strong>
              <br />
              <small class="text-muted">
                ID Organismo: {{ selectedEntity.organismo.organismoId }}
              </small>
            </div>
            <span class="text-muted" v-else>—</span>
          </dd>
        </dl>
      </div>
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel">Cerrar</b-button>
      </template>
    </b-modal>
  </b-overlay>
</template>

<script>
import { getMyEntities, getMyDocuments } from '@/febos/servicios/api/docDigital.api';

export default {
  name: 'MyEntities',
  data() {
    return {
      loading: false,
      entities: [],
      documents: [],
      // Paginación
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      totalPages: 1,
      fields: [
        { key: 'entidadId', label: 'ID Entidad', sortable: true },
        { key: 'entidadNombre', label: 'Nombre Entidad', sortable: true },
        { key: 'organismo', label: 'Organismo', sortable: true },
        { key: 'entidad_padre', label: 'Entidad Padre', sortable: false },
        { key: 'actions', label: 'Acciones' }
      ],
      showDetailModal: false,
      selectedEntity: null
    };
  },
  async created() {
    this.fetchMyEntities();
    this.fetchMyDocuments();
  },
  methods: {
    async fetchMyEntities() {
      this.loading = true;
      try {
        // Llamamos al endpoint para obtener las entidades asociadas
        const response = await getMyEntities({
          page: this.currentPage,
          perPage: this.perPage
        });

        // La respuesta tiene el formato:
        // {
        //   "respuesta": {
        //     "status": 200,
        //     "message": "OK",
        //     "count": 1,
        //     "timestamp": "24-07-2026 12:05:12",
        //     "result": [
        //       {
        //         "entidadId": 300,
        //         "entidadNombre": "Municipalidad de Machalí",
        //         "entidadPadreId": 647,
        //         "organismo": {
        //           "organismoId": 647,
        //           "organismoNombre": "Subsecretaría de Desarrollo Regional y Administrativo"
        //         }
        //       }
        //     ]
        //   },
        //   "codigo": 10,
        //   ...
        // }

        // La función getMyEntities devuelve el objeto `respuesta` (el interior)
        // Accedemos a response.result que es el array de entidades
        if (response && response.result && Array.isArray(response.result)) {
          this.entities = response.result;
          this.totalCount = response.count || response.result.length;
          this.totalPages = Math.ceil(this.totalCount / this.perPage);
        } else {
          this.entities = [];
          this.totalCount = 0;
          this.totalPages = 1;
        }
      } catch (error) {
        console.error('Error al cargar entidades:', error);
        this.$notify({
          group: 'notifications',
          type: 'error',
          title: 'Error',
          text: 'No se pudieron cargar las entidades asociadas'
        });
        this.entities = [];
        this.totalCount = 0;
        this.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },
    async fetchMyDocuments() {
      const response = await getMyDocuments({
        page: this.currentPage,
        perPage: this.perPage
      });
      if (response && response.result && Array.isArray(response.result)) {
        this.documents = response.result;
        this.totalCount = response.count || response.result.length;
        this.totalPages = Math.ceil(this.totalCount / this.perPage);
      } else {
        this.documents = [];
        this.totalCount = 0;
        this.totalPages = 1;
      }
    },
    async refreshList() {
      await this.fetchMyEntities();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchMyEntities();
    },

    viewEntityDetails(entity) {
      this.selectedEntity = entity;
      this.showDetailModal = true;
    }
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.badge {
  font-size: 0.75rem;
  font-weight: 500;
}

.table td {
  vertical-align: middle;
}

.spinner-border-sm {
  width: 0.75rem;
  height: 0.75rem;
}
</style>
